import type { BlockNode, ExperienceDocument, Theme } from '@personalizehq/schemas/ir';

// THE AUTHORING DOC — the mutable D1-side artifact (SPEC §2). It is NOT the wire bundle and is
// never sent to an SDK: `publish` compiles it. That split is what lets this shape churn weekly
// while no pinned binary notices, so nothing here needs to be conservative.
//
// It exists because `ExperienceDocument` is ONE surface + ONE root — there is no multi-step shape
// anywhere in the contract (p3-authoring-dashboard.md §1, gap 1). A two-step onboarding carousel
// is the canonical demo, so the authoring doc is `steps[]` from day one; the WIRE flow model
// lands in P4 with the compiler, before any binary pins.
//
// The SHAPE is defined once, in `@personalizehq/schemas/authoring`, because the doc crosses a trust
// boundary (`POST /api/experiences`) and the server has to validate it with zod. This import is
// type-only and erases at build, so this package stays zero-runtime-dep on zod.
export type { AdvanceRule, AuthoringDoc, Step } from '@personalizehq/schemas';

import type { AuthoringDoc, Step } from '@personalizehq/schemas';

export function newId(): string {
  return crypto.randomUUID();
}

/** An empty column stack — the smallest legal root a step can have. */
export function emptyRoot(): BlockNode {
  return {
    block: 'stack',
    id: newId(),
    direction: 'column',
    gap: 0,
    align: 'stretch',
    justify: 'start',
    children: [],
  };
}

export function createStep(overrides: Partial<Step> = {}): Step {
  return {
    id: newId(),
    name: 'Step 1',
    surface: 'modal',
    root: emptyRoot(),
    ...overrides,
  };
}

export function createDoc(theme: Theme, overrides: Partial<AuthoringDoc> = {}): AuthoringDoc {
  return {
    doc_version: 1,
    theme,
    steps: [createStep()],
    ...overrides,
  };
}

export function findStep(doc: AuthoringDoc, stepId: string): Step | undefined {
  return doc.steps.find((s) => s.id === stepId);
}

/** What the layers tree calls a node: the author's name if they gave it one, else a summary derived
 *  from the block itself. The derivation matters more than the rename does — a 12-node modal is
 *  perfectly readable as "vertical stack / text: Welcome back / button: Continue", and an author who
 *  never renames anything should never see a wall of "stack, stack, stack". */
export function nodeLabel(node: BlockNode, labels: AuthoringDoc['labels']): string {
  const named = labels?.[node.id]?.trim();
  if (named) return named;

  switch (node.block) {
    case 'stack':
      return node.direction === 'row' ? 'horizontal stack' : 'vertical stack';
    case 'text':
    case 'button': {
      const spans = node.block === 'text' ? node.spans : node.label;
      const first = spans.find((s) => s.kind === 'lit');
      const literal = first?.kind === 'lit' ? first.value.trim() : '';
      return literal ? `${node.block}: ${truncate(literal)}` : node.block;
    }
    case 'scroll':
      return `${node.axis} scroll`;
    case 'custom':
      return `custom: ${node.identifier}`;
    default:
      return node.block;
  }
}

function truncate(value: string): string {
  return value.length <= 24 ? value : `${value.slice(0, 23)}…`;
}

/** Rename in a draft. An empty name CLEARS the entry rather than storing `""` — otherwise the doc
 *  accumulates empty strings that the schema (min 1) would then reject at save. */
export function setLabel(doc: AuthoringDoc, nodeId: string, name: string): void {
  const trimmed = name.trim();
  if (!trimmed) {
    if (doc.labels) delete doc.labels[nodeId];
    return;
  }
  doc.labels ??= {};
  doc.labels[nodeId] = trimmed.slice(0, 80);
}

// The step ops. Like `tree.ts`, these MUTATE and are written to run inside an immer draft
// (`applyChange`), so the store turns each into a patch pair and undo comes for free.
//
// The bounds are `authoringDocSchema`'s, not opinions: steps is `.min(1).max(20)`. Enforcing them
// here means the editor cannot author a doc the server will reject at save.

export const MAX_STEPS = 20;

export function addStep(doc: AuthoringDoc, index?: number, overrides: Partial<Step> = {}): Step {
  if (doc.steps.length >= MAX_STEPS) throw new Error(`addStep: a doc may hold at most ${MAX_STEPS} steps`);
  const step = createStep({ name: `Step ${doc.steps.length + 1}`, ...overrides });
  const at = index === undefined ? doc.steps.length : clamp(index, doc.steps.length);
  doc.steps.splice(at, 0, step);
  return step;
}

export function removeStep(doc: AuthoringDoc, stepId: string): void {
  // A doc with zero steps is unsavable AND uncompilable — `stepToExperienceDocument` has nothing to
  // return. Refusing here is the difference between a disabled button and a broken document.
  if (doc.steps.length <= 1) throw new Error('removeStep: a doc must keep at least one step');
  const index = doc.steps.findIndex((s) => s.id === stepId);
  if (index === -1) throw new Error(`removeStep: unknown step ${stepId}`);
  doc.steps.splice(index, 1);
}

export function moveStep(doc: AuthoringDoc, stepId: string, index: number): void {
  const from = doc.steps.findIndex((s) => s.id === stepId);
  if (from === -1) throw new Error(`moveStep: unknown step ${stepId}`);
  const [step] = doc.steps.splice(from, 1);
  doc.steps.splice(clamp(index, doc.steps.length), 0, step as Step);
}

function clamp(index: number, length: number): number {
  if (!Number.isFinite(index)) return length;
  return Math.max(0, Math.min(Math.trunc(index), length));
}

/** The seam to the renderer: `renderer-web`'s `mount()` takes an `ExperienceDocument`, so the canvas
 *  renders EXACTLY what the SDK will render — no editor-only preview path, ever. */
export function stepToExperienceDocument(doc: AuthoringDoc, stepId: string): ExperienceDocument {
  const step = findStep(doc, stepId);
  if (!step) throw new Error(`unknown step: ${stepId}`);
  return {
    schema_version: 'v1',
    surface: step.surface,
    theme: doc.theme,
    root: step.root,
  };
}
